import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListTodo, HardDrive, Users, Settings, Sun, Moon, Crown, LogOut } from 'lucide-react';
import { useTheme } from './ManagerLayout';
import { usePlan } from '../../contexts/PlanContext';
import { useAuth } from '../../contexts/AuthContext';
import styles from './FloatingDock.module.css';

interface DockItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const dockItems: DockItem[] = [
  { id: 'queue', label: 'Queue', icon: <ListTodo size={20} />, path: '/dashboard' },
  { id: 'drive', label: 'Drive', icon: <HardDrive size={20} />, path: '/assets' },
  { id: 'clients', label: 'Clients', icon: <Users size={20} />, path: '/clients' },
  { id: 'settings', label: 'Settings', icon: <Settings size={20} />, path: '/settings' },
];

export function FloatingDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { currentPlan } = usePlan();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <motion.nav
      className={styles.dock}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className={styles.dockInner}>
        {/* Logo */}
        <div className={styles.logoSection}>
          <img
            src={isDark ? '/logo-light.png' : '/logo-dark.png'}
            alt="doopl"
            className={styles.logoIcon}
          />
        </div>

        <div className={styles.divider} />

        {/* Navigation */}
        {dockItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <button
              key={item.id}
              className={`${styles.dockItem} ${isActive ? styles.active : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className={styles.iconWrapper}>{item.icon}</span>
              <span className={styles.label}>{item.label}</span>
            </button>
          );
        })}

        <div className={styles.divider} />

        {/* Theme Toggle */}
        <button
          className={styles.themeToggle}
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Plan Badge */}
        <button
          className={`${styles.planBadge} ${styles[currentPlan.id]}`}
          onClick={() => navigate('/settings')}
          title={`${currentPlan.name} Plan - Click to manage`}
        >
          {currentPlan.id !== 'free' && <Crown size={12} />}
          <span>{currentPlan.name}</span>
        </button>

        <div className={styles.divider} />

        {/* Logout */}
        <button
          className={styles.logoutBtn}
          onClick={handleLogout}
          aria-label="Log out"
          title="Log out"
        >
          <LogOut size={16} />
        </button>
      </div>
    </motion.nav>
  );
}
